/**
 * Permission Configuration
 * Maps backend permissions to frontend routes and menu items
 */

export const PERMISSIONS = {
  // Warehouse Permissions
  SHOW_WAREHOUSE: "show-warehouse",
  CREATE_WAREHOUSE: "create-warehouse",
  UPDATE_WAREHOUSE: "update-warehouse",
  DELETE_WAREHOUSE: "delete-warehouse",

  // IP Permissions
  SHOW_IP: "show-ip",
  CREATE_IP: "create-ip",
  UPDATE_IP: "update-ip",
  DELETE_IP: "delete-ip",

  // Unit Classification Permissions
  SHOW_COMMODITY_CLASSIFICATION: "show-commodity-classification",
  CREATE_COMMODITY_CLASSIFICATION: "create-commodity-classification",
  UPDATE_COMMODITY_CLASSIFICATION: "update-commodity-classification",
  DELETE_COMMODITY_CLASSIFICATION: "delete-commodity-classification",

  // Fund Code Permissions
  SHOW_FUND_CODE: "show-fund-code",
  CREATE_FUND_CODE: "create-fund-code",
  UPDATE_FUND_CODE: "update-fund-code",
  DELETE_FUND_CODE: "delete-fund-code",

  // Shipment Permissions
  SHOW_SHIPMENT: "show-shipment",
  CREATE_SHIPMENT: "create-shipment",
  UPDATE_SHIPMENT: "update-shipment",
  DELETE_SHIPMENT: "delete-shipment",

  // Item Category Permissions
  SHOW_ITEM_CATEGORY: "show-item-category",
  CREATE_ITEM_CATEGORY: "create-item-category",
  UPDATE_ITEM_CATEGORY: "update-item-category",
  DELETE_ITEM_CATEGORY: "delete-item-category",

  // Item Permissions
  SHOW_ITEM: "show-item",
  CREATE_ITEM: "create-item",
  UPDATE_ITEM: "update-item",
  DELETE_ITEM: "delete-item",

  // Provider Permissions
  SHOW_PROVIDER: "show-provider",
  CREATE_PROVIDER: "create-provider",
  UPDATE_PROVIDER: "update-provider",
  DELETE_PROVIDER: "delete-provider",

  // Facility Permissions
  SHOW_FACILITY: "show-facility",
  CREATE_FACILITY: "create-facility",
  UPDATE_FACILITY: "update-facility",
  DELETE_FACILITY: "delete-facility",

  // Province Permissions
  SHOW_PROVINCE: "show-province",
  UPDATE_PROVINCE: "update-province",

  // Project Permissions
  SHOW_PROJECT: "show-project",
  CREATE_PROJECT: "create-project",
  UPDATE_PROJECT: "update-project",
  DELETE_PROJECT: "delete-project",

  // Provider In (Receipts) Permissions
  SHOW_PROVIDER_IN: "show-provider-in",
  CREATE_PROVIDER_IN: "create-provider-in",
  UPDATE_PROVIDER_IN: "update-provider-in",
  DELETE_PROVIDER_IN: "delete-provider-in",

  // Provider Out (Distributions) Permissions
  SHOW_PROVIDER_OUT: "show-provider-out",
  CREATE_PROVIDER_OUT: "create-provider-out",
  UPDATE_PROVIDER_OUT: "update-provider-out",
  DELETE_PROVIDER_OUT: "delete-provider-out",

  // Province In Permissions
  SHOW_PROVINCE_IN: "show-province-in",

  // Province Out (Distributions) Permissions
  SHOW_PROVINCE_OUT: "show-province-out",
  CREATE_PROVINCE_OUT: "create-province-out",
  UPDATE_PROVINCE_OUT: "update-province-out",
  DELETE_PROVINCE_OUT: "delete-province-out",

  // Currency Permissions
  SHOW_CURRENCY: "show-currency",
  CREATE_CURRENCY: "create-currency",
  UPDATE_CURRENCY: "update-currency",
  DELETE_CURRENCY: "delete-currency",

  // Adjustment Permissions
  SHOW_ADJUSTMENT: "show-adjustment",
  CREATE_ADJUSTMENT: "create-adjustment",
  UPDATE_ADJUSTMENT: "update-adjustment",
  DELETE_ADJUSTMENT: "delete-adjustment",

  // User Permissions
  SHOW_USER: "show-user",
  CREATE_USER: "create-user",
  UPDATE_USER: "update-user",
  DELETE_USER: "delete-user",
  MANAGE_USER_PERMISSION: "manage-user-permission",
  MANAGE_USER_ROLE: "manage-user-role",

  // Role Permissions
  SHOW_ROLE: "show-role",
  CREATE_ROLE: "create-role",
  UPDATE_ROLE: "update-role",
  DELETE_ROLE: "delete-role",
  MANAGE_ROLE_PERMISSION: "manage-role-permission",

  // Permission Permissions
  SHOW_PERMISSION: "show-permission",
  CREATE_PERMISSION: "create-permission",
  UPDATE_PERMISSION: "update-permission",
  DELETE_PERMISSION: "delete-permission",

  // Consumption Permissions
  SHOW_CONSUMPTION: "show-consumption",
  CREATE_CONSUMPTION: "create-consumption",
  UPDATE_CONSUMPTION: "update-consumption",
  DELETE_CONSUMPTION: "delete-consumption",
  EDIT_PREVIOUS_CONSUMPTION: "edit-previous-consumption",

  // Activity Log Permissions
  SHOW_ACTIVITY_LOG: "show-activity-log",

  // Report Permissions
  SHOW_REPORT: "show-report",
  EXPORT_REPORT: "export-report",

  // Dashboard Permissions
  SHOW_DASHBOARD: "show-dashboard",

  // Draft Permissions
  SHOW_PROVIDER_IN_DRAFT: "show-provider-in-draft",
  CREATE_PROVIDER_IN_DRAFT: "create-provider-in-draft",
  UPDATE_PROVIDER_IN_DRAFT: "update-provider-in-draft",
  DELETE_PROVIDER_IN_DRAFT: "delete-provider-in-draft",

  SHOW_PROVIDER_OUT_DRAFT: "show-provider-out-draft",
  CREATE_PROVIDER_OUT_DRAFT: "create-provider-out-draft",
  UPDATE_PROVIDER_OUT_DRAFT: "update-provider-out-draft",
  DELETE_PROVIDER_OUT_DRAFT: "delete-provider-out-draft",

  SHOW_PROVINCE_OUT_DRAFT: "show-province-out-draft",
  CREATE_PROVINCE_OUT_DRAFT: "create-province-out-draft",
  UPDATE_PROVINCE_OUT_DRAFT: "update-province-out-draft",
  DELETE_PROVINCE_OUT_DRAFT: "delete-province-out-draft",

  // Action Request Permissions
  SHOW_ACTION_REQUEST: "show-action-request",
  MANAGE_ACTION_REQUEST: "manage-action-request",

  // Return Request Permissions
  SHOW_RETURN_REQUEST: "show-return-request",
  CREATE_RETURN_REQUEST: "create-return-request",
  MANAGE_RETURN_REQUEST: "manage-return-request",
  SHOW_RETURNED_ITEMS: "show-returned-items",

  // Provider Transfer Permissions
  SHOW_PROVIDER_TRANSFER: "show-provider-transfer",
  CREATE_PROVIDER_TRANSFER: "create-provider-transfer",
  MANAGE_PROVIDER_TRANSFER: "manage-provider-transfer",
}

/**
 * Route to Permission Mapping
 * Maps frontend routes to required permissions
 */
export const ROUTE_PERMISSIONS = {
  // Admin Routes
  "/warehouse": PERMISSIONS.SHOW_WAREHOUSE,
  "/add-warehouse": PERMISSIONS.CREATE_WAREHOUSE,
  "/edit-warehouse/:id": PERMISSIONS.UPDATE_WAREHOUSE,

  "/providers": PERMISSIONS.SHOW_PROVIDER,
  "/add-provider": PERMISSIONS.CREATE_PROVIDER,
  "/edit-provider/:id": PERMISSIONS.UPDATE_PROVIDER,

  "/provinces": PERMISSIONS.SHOW_PROVINCE,
  "/edit-province/:id": PERMISSIONS.UPDATE_PROVINCE,

  "/facilities": PERMISSIONS.SHOW_FACILITY,
  "/add-facility": PERMISSIONS.CREATE_FACILITY,
  "/edit-facility/:id": PERMISSIONS.UPDATE_FACILITY,

  "/ips": PERMISSIONS.SHOW_IP,
  "/add-ip": PERMISSIONS.CREATE_IP,
  "/edit-ip/:id": PERMISSIONS.UPDATE_IP,

  "/commodity-classifications": PERMISSIONS.SHOW_COMMODITY_CLASSIFICATION,
  "/add-commodity-classification": PERMISSIONS.CREATE_COMMODITY_CLASSIFICATION,
  "/edit-commodity-classification/:id":
    PERMISSIONS.UPDATE_COMMODITY_CLASSIFICATION,

  "/fund-codes": PERMISSIONS.SHOW_FUND_CODE,
  "/add-fund-code": PERMISSIONS.CREATE_FUND_CODE,
  "/edit-fund-code/:id": PERMISSIONS.UPDATE_FUND_CODE,

  "/shipments": PERMISSIONS.SHOW_SHIPMENT,
  "/add-shipment": PERMISSIONS.CREATE_SHIPMENT,
  "/edit-shipment/:id": PERMISSIONS.UPDATE_SHIPMENT,

  "/item-categories": PERMISSIONS.SHOW_ITEM_CATEGORY,
  "/add-item-category": PERMISSIONS.CREATE_ITEM_CATEGORY,
  "/edit-item-category/:id": PERMISSIONS.UPDATE_ITEM_CATEGORY,

  "/items": PERMISSIONS.SHOW_ITEM,
  "/add-item": PERMISSIONS.CREATE_ITEM,
  "/edit-item/:id": PERMISSIONS.UPDATE_ITEM,

  "/users": PERMISSIONS.SHOW_USER,
  "/add-user": PERMISSIONS.CREATE_USER,
  "/edit-user/:id": PERMISSIONS.UPDATE_USER,

  "/roles": PERMISSIONS.SHOW_ROLE,
  "/add-role": PERMISSIONS.CREATE_ROLE,
  "/edit-role/:id": PERMISSIONS.UPDATE_ROLE,

  "/projects": PERMISSIONS.SHOW_PROJECT,
  "/add-project": PERMISSIONS.CREATE_PROJECT,
  "/edit-project/:id": PERMISSIONS.UPDATE_PROJECT,

  "/currencies": PERMISSIONS.SHOW_CURRENCY,
  "/add-currency": PERMISSIONS.CREATE_CURRENCY,
  "/edit-currency/:id": PERMISSIONS.UPDATE_CURRENCY,

  "/permissions": PERMISSIONS.SHOW_PERMISSION,
  "/add-permission": PERMISSIONS.CREATE_PERMISSION,
  "/edit-permission/:id": PERMISSIONS.UPDATE_PERMISSION,

  "/activity-logs": PERMISSIONS.SHOW_ACTIVITY_LOG,

  "/provider-receipt-action-requests": PERMISSIONS.MANAGE_ACTION_REQUEST,
  "/provider-distribution-action-requests": PERMISSIONS.MANAGE_ACTION_REQUEST,
  "/adjustment-action-requests": PERMISSIONS.MANAGE_ACTION_REQUEST,
  "/return-requests": PERMISSIONS.MANAGE_RETURN_REQUEST,
  "/admin/provider-transfers": PERMISSIONS.MANAGE_PROVIDER_TRANSFER,

  // Provider Routes
  "/provider-receipts": PERMISSIONS.SHOW_PROVIDER_IN,
  "/add-provider-receipts": PERMISSIONS.CREATE_PROVIDER_IN,
  "/edit-provider-receipts/:id": PERMISSIONS.UPDATE_PROVIDER_IN,
  "/edit-provider-receipts-draft/:id": PERMISSIONS.UPDATE_PROVIDER_IN_DRAFT,

  "/provider-distributions": PERMISSIONS.SHOW_PROVIDER_OUT,
  "/add-provider-distributions": PERMISSIONS.CREATE_PROVIDER_OUT,
  "/edit-provider-distributions/:id": PERMISSIONS.UPDATE_PROVIDER_OUT,

  "/provider-adjustments": PERMISSIONS.SHOW_ADJUSTMENT,
  "/add-provider-adjustments": PERMISSIONS.CREATE_ADJUSTMENT,
  "/edit-provider-adjustments/:id": PERMISSIONS.UPDATE_ADJUSTMENT,

  "/provider-consumptions": PERMISSIONS.SHOW_CONSUMPTION,
  "/provider-consumptions-reports": PERMISSIONS.SHOW_REPORT,
  "/provider-returned-items": PERMISSIONS.SHOW_RETURNED_ITEMS,
  "/provider-transfers": PERMISSIONS.SHOW_PROVIDER_TRANSFER,

  // Province Routes
  "/province-receipts": PERMISSIONS.SHOW_PROVINCE_IN,

  "/province-distributions": PERMISSIONS.SHOW_PROVINCE_OUT,
  "/add-province-distributions": PERMISSIONS.CREATE_PROVINCE_OUT,
  "/edit-province-distributions/:id": PERMISSIONS.UPDATE_PROVINCE_OUT,

  "/province-consumptions": PERMISSIONS.SHOW_CONSUMPTION,
  "/province-consumptions-reports": PERMISSIONS.SHOW_REPORT,
}

/**
 * Menu Item to Permission Mapping
 * For navbar menu items that need permission checks
 */
export const MENU_PERMISSIONS = {
  // Admin Menu Items
  warehouse: PERMISSIONS.SHOW_WAREHOUSE,
  projects: PERMISSIONS.SHOW_PROJECT,
  providers: PERMISSIONS.SHOW_PROVIDER,
  provinces: PERMISSIONS.SHOW_PROVINCE,
  currencies: PERMISSIONS.SHOW_CURRENCY,
  "fund-codes": PERMISSIONS.SHOW_FUND_CODE,
  shipments: PERMISSIONS.SHOW_SHIPMENT,
  facilities: PERMISSIONS.SHOW_FACILITY,
  ips: PERMISSIONS.SHOW_IP,
  "item-categories": PERMISSIONS.SHOW_ITEM_CATEGORY,
  items: PERMISSIONS.SHOW_ITEM,
  users: PERMISSIONS.SHOW_USER,
  roles: PERMISSIONS.SHOW_ROLE,
  permissions: PERMISSIONS.SHOW_PERMISSION,
  "activity-logs": PERMISSIONS.SHOW_ACTIVITY_LOG,
  "action-requests": PERMISSIONS.MANAGE_ACTION_REQUEST,
  "return-requests": PERMISSIONS.MANAGE_RETURN_REQUEST,
  "admin-provider-transfers": PERMISSIONS.MANAGE_PROVIDER_TRANSFER,

  // Provider Menu Items
  "provider-receipts": PERMISSIONS.SHOW_PROVIDER_IN,
  "provider-distributions": PERMISSIONS.SHOW_PROVIDER_OUT,
  "provider-adjustments": PERMISSIONS.SHOW_ADJUSTMENT,
  "provider-consumptions": PERMISSIONS.SHOW_CONSUMPTION,
  "provider-reports": PERMISSIONS.SHOW_REPORT,
  "provider-returned-items": PERMISSIONS.SHOW_RETURNED_ITEMS,
  "provider-transfers": PERMISSIONS.SHOW_PROVIDER_TRANSFER,

  // Province Menu Items
  "province-receipts": PERMISSIONS.SHOW_PROVINCE_IN,
  "province-distributions": PERMISSIONS.SHOW_PROVINCE_OUT,
  "province-consumptions": PERMISSIONS.SHOW_CONSUMPTION,
  "province-reports": PERMISSIONS.SHOW_REPORT,
}

/**
 * Check if user has permission
 * @param {Array} userPermissions - Array of user's permissions from Redux
 * @param {String} requiredPermission - Permission to check
 * @returns {Boolean}
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!requiredPermission) return true // No permission required
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  return userPermissions.includes(requiredPermission)
}

/**
 * Check if user has any of the permissions
 * @param {Array} userPermissions - Array of user's permissions
 * @param {Array} requiredPermissions - Array of permissions to check (OR logic)
 * @returns {Boolean}
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  return requiredPermissions.some(permission =>
    userPermissions.includes(permission)
  )
}

/**
 * Check if user has all of the permissions
 * @param {Array} userPermissions - Array of user's permissions
 * @param {Array} requiredPermissions - Array of permissions to check (AND logic)
 * @returns {Boolean}
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true
  if (!userPermissions || !Array.isArray(userPermissions)) return false
  return requiredPermissions.every(permission =>
    userPermissions.includes(permission)
  )
}
